import { Injectable } from '@angular/core';
import { STORAGE_NAME } from '../constants/constants';
import jwt_decode from 'jwt-decode';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { firstValueFrom, tap } from 'rxjs';
import { generateCodeChallenge, generateCodeVerifier } from '../utils/utils';
import {CookieService} from "@app/core/services/cookie.service";

export interface ChangePasswordResponse {
  code: string;
  message: string;
  data: {
    duration: string;
    email: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  decodedAccessToken: any;
  private roles: string[] | null = null;

  constructor(private http: HttpClient, private cookieService: CookieService) {}

  isAuthenticated(): any {
    const accessToken = this.cookieService.get(STORAGE_NAME.ACCESS_TOKEN);
    if (accessToken) {
      this.decodedAccessToken = jwt_decode(accessToken);
      return accessToken && (this.decodedAccessToken.exp * 1000 - new Date().getTime() > 0);
    }
  }

  getAccessToken(code: string) {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', `${environment.frontend}/callback`)
      .set('client_id', environment.clientId)
      .set('code_verifier', this.cookieService.get(STORAGE_NAME.PKCE_CODE_VERIFIER) || '')
      .set('scope', 'openid profile');

    return this.http.post<any>(`${environment.backend.sso}/oauth2/token`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  refreshToken() {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', environment.clientId)
      .set('refresh_token', this.cookieService.get(STORAGE_NAME.REFRESH_TOKEN));

    return this.http.post<any>(`${environment.backend.sso}/oauth2/token`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(tap(res => {
      this.cookieService.set(STORAGE_NAME.ACCESS_TOKEN, res.access_token);
      this.cookieService.set(STORAGE_NAME.REFRESH_TOKEN, res.refresh_token);
      this.cookieService.set(STORAGE_NAME.EXPIRES_AT, `${Date.now() + res.expires_in * 1000}`);
    }));
  }

  logout() {
      this.cookieService.deleteAll();
      window.location.href = `${environment.backend.sso}/v1/logout?` +
        'post_logout_redirect_uri=' + encodeURIComponent(`${environment.frontend}`) +
        '&client_id=' + environment.clientId;
  }

  public changePassword(data: any) {
    return this.http.post<ChangePasswordResponse>(`${environment.backend.sso}/v1/user/change-password`, data);
  }

  public forgotPassword(data: any) {
    return this.http.post<ChangePasswordResponse>(`${environment.backend.sso}/v1/user/forgot-password`, data);
  }

  // ===== 2FA (TOTP / Authenticator) — dùng cookie session của phiên chờ MFA =====
  public mfaSetup() {
    return this.http.get<any>(`${environment.backend.sso}/v1/user/mfa/setup`, { withCredentials: true });
  }

  public mfaEnable(code: string) {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/mfa/enable`, { code }, { withCredentials: true });
  }

  public verifyMfa(code: string) {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/mfa/verify`, { code }, { withCredentials: true });
  }

  public mfaRecovery(recoveryCode: string) {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/mfa/recovery`, { recoveryCode }, { withCredentials: true });
  }

  /** Tài khoản đang đăng nhập đã bật 2FA chưa — dùng để hiện/ẩn ô mã 2FA ngay (vd đổi mật khẩu). */
  public mfaStatus() {
    return this.http.get<any>(`${environment.backend.sso}/v1/user/mfa/status`);
  }

  /** Step-up: tái xác thực 2FA cho thao tác nhạy cảm (user đã đăng nhập). */
  public stepUp(code: string) {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/mfa/step-up`, { code }, { withCredentials: true });
  }

  // ===== Xác nhận email cá nhân / SĐT (session sau login, trước token) =====
  public getMyContact() {
    return this.http.get<any>(`${environment.backend.sso}/v1/user/me/contact`, { withCredentials: true });
  }

  public confirmContact() {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/me/contact/confirm`, {}, { withCredentials: true });
  }

  public sendContactOtp(personalEmail: string) {
    return this.http.post<any>(
      `${environment.backend.sso}/v1/user/me/contact/otp`,
      { personalEmail },
      { withCredentials: true }
    );
  }

  public updateContact(payload: { personalEmail: string; mobileNumber: string; otpCode: string }) {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/me/contact`, payload, { withCredentials: true });
  }

  /** Hủy HTTP session (dùng khi chưa có token, vd. màn confirm-contact) */
  public logoutSession() {
    return this.http.post<any>(`${environment.backend.sso}/v1/user/logout`, {}, { withCredentials: true });
  }

  async checkIsLogin() {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    this.cookieService.set(STORAGE_NAME.PKCE_CODE_VERIFIER, codeVerifier);

    const requestParams = new URLSearchParams({
      response_type: 'code',
      client_id: environment.clientId,
      redirect_uri: `${environment.frontend}/callback`,
      scope: 'openid profile email offline_access',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    })

    window.location.href = `${environment.backend.sso}/oauth2/authorize?${requestParams.toString()}`;
  }

  private async loadRolesFromUserInfo(): Promise<string[]> {
    console.log('Loading user roles from userinfo endpoint...');
    console.log('Current roles cache:', this.roles);
    if (this.roles) {
      return this.roles;
    }
    const accessToken = this.cookieService.get(STORAGE_NAME.ACCESS_TOKEN);
    if (!accessToken) {
      return [];
    }
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${environment.backend.sso}/userinfo`)
      );
      const rolesClaim = res?.roles;
      let roles: string[] = [];
      if (Array.isArray(rolesClaim)) {
        roles = rolesClaim;
      } else if (typeof rolesClaim === 'string') {
        roles = rolesClaim.split(',').map((r: string) => r.trim()).filter(Boolean);
      }
      this.roles = roles;
      return roles;
    } catch {
      console.error('Failed to load user roles from userinfo endpoint');
      return [];
    }
  }

  async hasAnyRole(required: string[]): Promise<boolean> {
    const roles = await this.loadRolesFromUserInfo();
    return required.some(r => roles.includes(r));
  }

}
